import { useEffect, useMemo, useState } from 'react';

import { ActionCreatorWithPayload } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  DriverRead,
  useDriversListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { MultiSelectFilterPopover } from 'src/common/primitives/FilterPopover/MultiSelectFilterPopover';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import { CoreFormFieldSwitch } from '../FormField/v2/core/CoreFormFieldSwitch';

interface DriverColumnFilterProps {
  open: boolean;
  onClose: () => void;
  selected: string[];
  setSelectedAction: ActionCreatorWithPayload<string[], string>;
  includeNoteFilter?: boolean;
  setIncludeNoteAction?: ActionCreatorWithPayload<boolean, string>;
}

export function DriverColumnFilter({
  open,
  onClose,
  selected,
  setSelectedAction,
  setIncludeNoteAction,
  includeNoteFilter = false,
}: DriverColumnFilterProps) {
  const dispatch = useAppDispatch();
  const includeNoteInitialState = useAppSelector(
    (state) => state.trip.filterIncludeNote
  );

  const [includeNote, setIncludeNote] = useState(includeNoteInitialState);
  const [searchTerm, setSearchTerm] = useState('');

  const { isRideOrderingPortalUser } = useUserRoles();
  const { data } = useDriversListQuery(
    { search: searchTerm },
    { skip: isRideOrderingPortalUser }
  );

  const selectedValues: DriverRead[] | undefined = useMemo(() => {
    return data?.results.filter((v) => selected.includes(v.id));
  }, [data, selected]);

  // Keep filter state in sync with redux state
  useEffect(() => {
    setIncludeNote(includeNoteInitialState);
  }, [includeNoteInitialState]);

  return (
    <MultiSelectFilterPopover
      open={open}
      value={selectedValues}
      filters={data?.results ?? []}
      renderLabel={({ first_name, last_name }) => `${first_name} ${last_name}`}
      onSearch={setSearchTerm}
      onCancel={onClose}
      onChange={(values) => {
        dispatch(setSelectedAction(values.map((v) => v.id)));
        if (includeNoteFilter && setIncludeNoteAction) {
          dispatch(setIncludeNoteAction(includeNote));
        }
      }}
      triggerElement={<div data-testid="driver-column-filter-trigger" />}
    >
      {includeNoteFilter && (
        <CoreFormFieldSwitch
          label="Include trips with driver entered note"
          inputProps={{
            value: includeNote,
            onChange: () => setIncludeNote(!includeNote),
          }}
        />
      )}
    </MultiSelectFilterPopover>
  );
}
